import { z } from "zod";

export const loginSchema = z.object({
  email: z.string().email({ message: "Enter a valid email" }),
  password: z.string().min(8, { message: "Password is required" })
});

const strictPasswordSchema = z
  .string()
  .min(12, { message: "Min 12 characters" })
  .regex(/[A-Z]/, { message: "Include uppercase" })
  .regex(/[0-9]/, { message: "Include number" })
  .regex(/[\W_]/, { message: "Include symbol" });

const optionalPasswordSchema = z.union([strictPasswordSchema, z.literal(""), z.undefined()]);

const agentBaseObject = z.object({
  id: z.string().optional(),
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Valid email required" }),
  role: z.enum(["owner", "admin", "manager", "agent", "viewer"], {
    required_error: "Role is required"
  }),
  password: optionalPasswordSchema,
  confirmPassword: optionalPasswordSchema
});

type AgentBaseShape = z.infer<typeof agentBaseObject>;

const passwordsMatch = (data: { password?: string; confirmPassword?: string }) => {
  if (!data.password && !data.confirmPassword) return true;
  return data.password === data.confirmPassword;
};

const withPasswordMatch = <T extends z.ZodTypeAny>(schema: T) =>
  schema.refine(passwordsMatch, {
    path: ["confirmPassword"],
    message: "Passwords must match"
  });

export const agentUpdateFormSchema = withPasswordMatch(agentBaseObject);

export const agentCreateFormSchema = withPasswordMatch(
  agentBaseObject.extend({
    password: strictPasswordSchema,
    confirmPassword: strictPasswordSchema
  })
);

export const widgetFormSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(3, { message: "Name must be at least 3 characters" }),
  description: z.string().max(256, { message: "Description too long" }).optional(),
  status: z.enum(["draft", "active", "archived"]).optional()
});

